/**
 * 摄像头现拍控件（证件照 / 身份证 / 人脸）
 * -----------------------------------------------------------------------------
 * 口径变更（2026-10-26 用户裁决）：**证件照也允许从相册选图**。
 * 原口径「必须现拍」在微信里根本落不了地（平台不支持 `getUserMedia`，没有取景画面），
 * 真实度改由 ④ 头像比对（1:1 比对身份证头像）承担 —— 选别人的照片过不了比对。
 * `captureSource` 仍恒传 `CAMERA`（服务端只认这个值），因此审计上区分不出
 * 「现拍」与「选图」，这点记在 `tasks/T-08-身份核验链.md`。
 *
 * 交互形态（设计稿第 03 块）：
 *   平时只渲染一个**紧凑触发器**（92 高的上传条 / 82×110 的照片框），
 *   点击 → 打开全屏取景浮层 → 拍摄 → 回调 → 自动收起。
 *   ⚠️ 不能把 `<video>` 常驻在卡片里：取流后是一块满屏黑框，
 *   既没有「点这儿拍」的意图，也会把卡片撑到半屏以上。
 *
 * 四道防线：
 *   ⓪ 前端：**先判环境能不能开实时取景**（`lib/camera-env.ts`）。真机调不起相机的
 *      两大成因都不在代码里 —— 内网 http 属非安全上下文（手机浏览器直接不暴露
 *      `mediaDevices`），微信内置浏览器则根本不支持取流。这两处**必须直接降级**
 *      到系统相机 / 选择器，而不是先弹一个没有画面的取景浮层让学员以为坏了。
 *   ① 前端：能取流就 `getUserMedia`；降级用两个常驻的 `<input type="file">` ——
 *      一个**只带 `capture`**（移动端直进相机），一个**带 `accept="image/*"`**
 *      （微信里弹「拍照 / 从相册选择」）。`capture` 跟随 `facing`：
 *      自拍（证件照 / 人脸）要前置，证件扫描要后置，写死后置会让自拍只能拍反。
 *   ② 前端：**按取景框裁切**（`photo-crop.ts`）—— 证件照按框**外扩 30%** 裁、
 *      保留原生像素；身份证按框外扩 12% 后裁。证件照的 67% 头高构图**不在这里做**：
 *      它要同时知道发顶与下颚，而发顶一旦出框就永久丢失，所以由服务端按关键点重裁
 *      （口径变更见 `photo-crop.ts` 文件头）。**降级路径（系统相机 / 选择器）不裁** ——
 *      那几张是成品图，没有框可依，硬裁会把证件切歪。
 *   ③ 服务端：`POST /{id}/photo` 携带 `captureSource=CAMERA`，非 CAMERA 一律 `KYC-301`。
 *      前端被绕过时拦在这里。
 *
 * 按钮矩阵（2026-10-25 拍板，只按「能否取流 × 是否允许相册」两轴展开）：
 *   | 能取流 | 允许相册 | 按钮                                        |
 *   |--------|----------|---------------------------------------------|
 *   | 是     | 是       | 「相册」+「立即拍摄」                        |
 *   | 是     | 否       | 「立即拍摄」                                 |
 *   | 否     | 是·微信  | **只有「相册」**                             |
 *   | 否     | 是·非微信| 「系统相机」                                 |
 *   | 否     | 否       | 「立即拍摄」（直进系统相机）                  |
 *
 * 当前唯一的调用方 `KycFlow`（证件照）与 `IdCardField`（身份证）**都传 `allowAlbum`**，
 * 所以实际只会落到「允许相册」那三行；「否」那两行是留给强制现拍场景的能力位。
 *
 * 微信内之所以只留一个：点带 `accept` 的文件选择器时，微信会弹「拍照 / 从相册选择」
 * 两个选项 —— **一个按钮就把系统相机与相册都覆盖了**，再并排放一个「系统相机」
 * 只是把同一个入口说了两遍。非微信没有这个合并菜单（`accept` 直接进图库），
 * 所以那边保持直进相机的「系统相机」。
 */
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  describeLiveCaptureFallback,
  detectCameraEnv,
  resolveFallbackAction,
} from '../../lib/camera-env';
import { normalizeImage } from '../../lib/image-normalize';
import { useDismissBackGuard } from '../../lib/use-dismiss-back-guard';
import { CameraIcon } from '../icons';
import { Button } from '../layout';
import {
  applyCropRule,
  CAPTURE_CROP_RULE,
  type CaptureMask,
  mapFrameToVideoCrop,
  resolveCropOutput,
} from './photo-crop';

export type { CaptureMask };

/**
 * 降级形态下示例蒙版下方的那一句**构图要求**。
 *
 * 为什么每个面各写一句而不是共用一句：`hint` 走的是「此刻该做什么」（放哪个面 / 怎么站），
 * 这句话走的是「拍成什么样算合格」。降级时学员开着系统相机，看不到取景框，
 * 只有具体要求能在拍之前提醒他「四角要全」或「肩要露出来」。
 */
const FALLBACK_TIP: Readonly<Record<CaptureMask | 'none', string>> = {
  portrait: '头部居中放虚线内，露出双肩',
  'id-front': '人像面朝上，四角完整入框',
  'id-back': '国徽面朝上，四角完整入框',
  none: '按示例取景，保持画面完整',
};

export interface CameraCaptureProps {
  /** 拍照完成回调（JPEG Blob） */
  onCapture: (image: Blob) => void;
  /** 拍摄说明（如「请将身份证人像面放入取景框」） */
  hint: string;
  /** 前景摄像头 or 后置摄像头 */
  facing?: 'user' | 'environment';
  /** 取景浮层里的快门文案 */
  actionText?: string;
  disabled?: boolean;
  /** 卡片里的触发器内容（按钮形态由外部给） */
  children: ReactNode;
  /** 触发器额外的 class（上传条 / 照片框） */
  triggerClassName?: string;
  /**
   * 取景蒙版（人像面画头像框与文字区，国徽面画国徽位）。
   *
   * **它同时决定成片怎么裁，但不再是最终构图**：`portrait` → 按框外扩 30% 裁、
   * 保留原生像素；`id-front` / `id-back` → 按框外扩 12% 后裁；不传 → 整帧不裁。
   * 证件照的 67% 头高由服务端重裁 —— 这里只是「把人摆对位置」的引导。
   */
  mask?: CaptureMask;
  /**
   * 是否放行相册。
   *
   * 2026-10-26 起**两个调用方都开**（证件照 + 身份证）：微信内没有实时取景，
   * 不开相册等于证件照完全拍不了。真实度改由 ④ 头像比对承担。
   * 保留这个开关是为了给「强制现拍」留能力位，当前无调用方传 false。
   */
  allowAlbum?: boolean;
}

function toJpegBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('画面生成失败，请重试'));
      },
      'image/jpeg',
      0.92,
    );
  });
}

/**
 * 蒙版图形：`viewBox 159×100` ≈ 身份证 85.6×54。
 *
 * 两张面**必须一眼能分**（不是「都是个圆角框」）：
 *   - 人像面：右侧头像框 + 姓名 / 性别民族 / 出生 / 住址 四行 + 底部公民身份号码长虚线
 *   - 国徽面：顶部正中圆形国徽 + 签发机关 / 有效期限两行 + 底部「中华人民共和国居民身份证」
 * 每张都带角标文字（人像面 / 国徽面），避免用户把国徽面放反了还在等快门。
 */
function MaskArtwork({ variant }: { variant: Exclude<CaptureMask, 'portrait'> }) {
  const line = 'rgba(255,255,255,0.34)';
  const dash = 'rgba(255,255,255,0.6)';
  const label = 'rgba(255,255,255,0.5)';
  const labelProps = { fontSize: 4.6, fill: label } as const;
  return (
    <svg
      className="camera-overlay__mask"
      viewBox="0 0 159 100"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
    >
      <text x="7" y="11" fontSize="5.4" fill={label} letterSpacing="0.4">
        {variant === 'id-front' ? '人像面' : '国徽面'}
      </text>

      {variant === 'id-front' ? (
        <>
          {/* 头像位（真实证件在右侧） */}
          <rect
            x="112"
            y="15"
            width="34"
            height="45"
            rx="3"
            fill="none"
            stroke={dash}
            strokeWidth="1.4"
            strokeDasharray="3 2.6"
          />
          <g fill="none" stroke={dash} strokeWidth="1.2">
            <circle cx="129" cy="31" r="5.4" />
            <path d="M120.6 48.5a8.6 8.6 0 0 1 16.8 0" />
          </g>

          {/* 姓名 / 性别 + 民族 / 出生 / 住址（两行） */}
          <text x="7" y="25" {...labelProps}>
            姓名
          </text>
          <text x="7" y="39" {...labelProps}>
            性别
          </text>
          <text x="63" y="39" {...labelProps}>
            民族
          </text>
          <text x="7" y="53" {...labelProps}>
            出生
          </text>
          <text x="7" y="67" {...labelProps}>
            住址
          </text>
          <g stroke={line} strokeWidth="1.8" strokeLinecap="round">
            <line x1="24" y1="23.4" x2="104" y2="23.4" />
            <line x1="24" y1="37.4" x2="56" y2="37.4" />
            <line x1="80" y1="37.4" x2="104" y2="37.4" />
            <line x1="24" y1="51.4" x2="104" y2="51.4" />
            <line x1="24" y1="65.4" x2="104" y2="65.4" />
            <line x1="24" y1="74.4" x2="94" y2="74.4" />
          </g>

          {/* 公民身份号码 */}
          <text x="7" y="92" {...labelProps}>
            公民身份号码
          </text>
          <line
            x1="44"
            y1="90.4"
            x2="144"
            y2="90.4"
            stroke={dash}
            strokeWidth="1.4"
            strokeDasharray="2.6 2.2"
          />
        </>
      ) : (
        <>
          {/* 国徽：顶部正中 */}
          <circle
            cx="79.5"
            cy="27"
            r="10.5"
            fill="none"
            stroke={dash}
            strokeWidth="1.4"
            strokeDasharray="3 2.6"
          />
          <path
            d="M79.5 21.6 81 25.4l4 .3-3.1 2.6 1 3.9-3.4-2.2-3.4 2.2 1-3.9-3.1-2.6 4-.3Z"
            fill="none"
            stroke={dash}
            strokeWidth="1"
            strokeLinejoin="round"
          />

          {/* 签发机关 / 有效期限 */}
          <text x="40" y="53" {...labelProps}>
            签发机关
          </text>
          <text x="40" y="65" {...labelProps}>
            有效期限
          </text>
          <g stroke={line} strokeWidth="1.8" strokeLinecap="round">
            <line x1="64" y1="51.4" x2="122" y2="51.4" />
            <line x1="64" y1="63.4" x2="122" y2="63.4" />
          </g>

          <text x="79.5" y="86" fontSize="5.2" fill={label} textAnchor="middle" letterSpacing="0.6">
            中华人民共和国居民身份证
          </text>
        </>
      )}
    </svg>
  );
}

/**
 * 职业证件照的**头肩蒙版**（一寸 25×35mm）。
 * -----------------------------------------------------------------------------
 * 交互意图：框外压暗到看不清，框内也只留**头肩形状**透出视频画面，
 * 学员把脸摆进虚线里就说明构图到位 —— 不需要理解「像素」「比例」这类词。
 *
 * 几何（`viewBox 50×70`，把 25×35mm 按 1mm = 2 单位换算，故与画幅严格同比例）：
 *   - 头顶留白 3.5mm（y=7）：留太少会顶到边、留太多会显头小
 *   - 发顶到下颚 23.5mm（y=7→54）：证件照惯例头部约占画面高 2/3
 *   - 头部宽度 16.8mm（rx=16.8，两侧共 33.6）：约画面宽的 2/3
 *   - 肩线最高 28mm（y=56）向两侧展开到底边：这是「露肩」的下限，
 *     没有肩部会拍成大头照，换底/裁切时无处延展
 *
 * ⚠️ 蒙版**只是构图引导**：`shoot()` 会拿这个框的 `getBoundingClientRect()` 换算出
 * video 内部像素矩形，按它**外扩一圈**裁（见 `photo-crop.ts`）。最终的 67% 头高
 * 构图由服务端按「下颚（检测）+ 发顶（分割遮罩）」重算 —— 框的尺寸仍在影响
 * 上传画面的范围与分辨率，所以改框时仍要复核成片头部是否够大（>240px）。
 */
function PortraitMask() {
  // 头肩形状 → 用一个 mask 挖空（黑=隐藏）。分成多个形状也不会出现
  // evenodd 那种「重叠区又变回暗色」的斑块问题。
  return (
    <svg
      className="camera-overlay__mask"
      viewBox="0 0 50 70"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
    >
      <defs>
        <mask id="h5-portrait-hole" maskUnits="userSpaceOnUse" x="0" y="0" width="50" height="70">
          <rect x="0" y="0" width="50" height="70" fill="#fff" />
          <ellipse cx="25" cy="30.5" rx="16.8" ry="23.5" fill="#000" />
          {/*
            颈 + 肩做成**一条连体路径**，且与椭圆下缘重叠（44 < 54）。
            分成「头」和「肩」两个形状会在两者之间留一条未挖空的暗带，
            看起来像人头浮在肩上 —— 这是第一版真踩到的。
          */}
          <path
            d="M19.5 44 C19.5 51 16 53.5 11 55 C5.5 56.8 1 61.5 1 70 L49 70 C49 61.5 44.5 56.8 39 55 C34 53.5 30.5 51 30.5 44 Z"
            fill="#000"
          />
        </mask>
      </defs>

      {/* 框内压暗：只在头肩之外生效 */}
      <rect
        x="0"
        y="0"
        width="50"
        height="70"
        fill="rgba(6,10,20,0.52)"
        mask="url(#h5-portrait-hole)"
      />

      {/*
        引导轮廓（虚线）：只描「头 + 肩」的外缘。
        - 肩线末端取椭圆下缘的交点（x=±5.5 处 y≈52.6），不往脸内延，
          否则那两段会像触角一样伸进脸里
        - 肩外缘贴到 1 / 49：真人肩膀会顶出画面，留缝会让人以为肩要往里收
      */}
      <g fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="0.5" strokeDasharray="2 1.6">
        <ellipse cx="25" cy="30.5" rx="16.8" ry="23.5" />
        <path d="M1 70 C1 61.5 5.5 56.8 11 55 C15.5 53.6 18.4 53.4 19.6 52.6" />
        <path d="M49 70 C49 61.5 44.5 56.8 39 55 C34.5 53.6 31.6 53.4 30.4 52.6" />
      </g>
    </svg>
  );
}

export function CameraCapture({
  onCapture,
  hint,
  facing = 'environment',
  actionText = '立即拍摄',
  disabled = false,
  children,
  triggerClassName = '',
  mask,
  allowAlbum = false,
}: CameraCaptureProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  /** 取景框节点：裁切要靠它的实际布局，不能用 CSS 里的百分比反推 */
  const frameRef = useRef<HTMLDivElement | null>(null);
  /** 系统相机入口（带 capture，不含 accept） */
  const fileRef = useRef<HTMLInputElement | null>(null);
  /** 相册入口（带 accept，不含 capture） */
  const albumRef = useRef<HTMLInputElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [open, setOpen] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);
  /** 相册 / 系统相机拿到的原图正在归一化（几百毫秒，但手机大图上会到 1~2 秒） */
  const [preparing, setPreparing] = useState(false);
  /** 环境能力只在挂载时判一次：UA / 安全上下文在一次会话里不会变 */
  const env = useMemo(() => detectCameraEnv(), []);

  const stopStream = useCallback(() => {
    for (const track of streamRef.current?.getTracks() ?? []) track.stop();
    streamRef.current = null;
    setStreaming(false);
  }, []);

  useEffect(() => stopStream, [stopStream]);

  const close = useCallback(() => {
    stopStream();
    setOpen(false);
    setErrorText(null);
  }, [stopStream]);

  /**
   * 浮层占一层返回守卫：左缘右滑 / 安卓返回键 = **关掉取景页**，
   * 而不是穿到表单层的「退出填写？」上（2026-10-26 真机反馈的原话：
   * 「取景示例页面返回也应该是返回表单页面啊，为什么直接弹出退出填写了」）。
   *
   * 只在 `open` 为真时占位。`allowAlbum === false` 那条路径不开浮层、直接拉系统相机，
   * 没有可关的东西，自然也不需要守卫。
   */
  useDismissBackGuard(open, close);

  const startCamera = useCallback(async () => {
    setErrorText(null);
    if (!navigator.mediaDevices?.getUserMedia) {
      setErrorText('当前浏览器不支持实时拍摄，已改为调用系统相机');
      fileRef.current?.click();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facing, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => undefined);
      }
      setStreaming(true);
    } catch {
      // 权限被拒 / 无可用摄像头：这时下方会多出一个「系统相机」兜底按钮，
      // 让学员还有一条真能拍的路，而不是盯着黑屏反复点快门
      setErrorText('无法打开摄像头，请检查相机权限后重试，或点击下方「系统相机」');
    }
  }, [facing]);

  /**
   * 降级形态（没有实时取景）下的**唯一**动作。
   *
   * 一个按钮而不是两个 —— 理由见文件头「按钮矩阵」。判定本身在
   * `resolveFallbackAction`（纯函数、有单测），这里只负责把 `target` 接到
   * 对应的隐藏 `<input>` 上：能跑分支判定的地方就别把 if 埋在 JSX 里。
   */
  const fallbackAction = useMemo(
    () => resolveFallbackAction({ isWechat: env.isWechat, allowAlbum, actionText }),
    [actionText, allowAlbum, env.isWechat],
  );

  const pickFallback = useCallback(() => {
    if (fallbackAction.target === 'album') albumRef.current?.click();
    else fileRef.current?.click();
  }, [fallbackAction.target]);

  /**
   * 点触发器 → 分流。
   *
   * 不能开实时取景时**一律不弹空取景台**：浮层里没有画面，只剩一块黑，
   * 学员的第一反应是「坏了」，而我们其实还有一条能用的路。
   *   - 允许相册（身份证 / 证件照，当前两个调用方都是）→ 开浮层，把 `hint`
   *     （拍哪一面 / 怎么站）先读一遍，浮层里**只有一个**降级按钮
   *     （微信内「相册」/ 非微信「系统相机」）
   *   - 强制现拍（`allowAlbum=false`，当前无调用方）→ 不开浮层，直接拉起系统相机
   */
  const openCamera = useCallback(() => {
    if (disabled) return;
    if (!env.canLiveCapture) {
      if (allowAlbum) setOpen(true);
      else fileRef.current?.click();
      return;
    }
    setOpen(true);
    void startCamera();
  }, [allowAlbum, disabled, env.canLiveCapture, startCamera]);

  const shoot = useCallback(async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !streaming) return;
    const context = canvas.getContext('2d');
    if (!context) {
      setErrorText('画面生成失败，请重试');
      return;
    }

    const videoSize = {
      width: video.videoWidth || 720,
      height: video.videoHeight || 1280,
    };
    const rule = mask ? CAPTURE_CROP_RULE[mask] : null;
    const frameRect = frameRef.current?.getBoundingClientRect();

    // 有蒙版且框已上屏 → 按框裁；否则整帧（无框场景与布局异常时的兜底）
    let crop = { x: 0, y: 0, width: videoSize.width, height: videoSize.height };
    if (rule && frameRect && frameRect.width > 0 && frameRect.height > 0) {
      crop = applyCropRule(
        mapFrameToVideoCrop({
          frame: frameRect,
          video: video.getBoundingClientRect(),
          videoSize,
        }),
        rule,
        videoSize,
      );
    }
    const output = rule
      ? resolveCropOutput(crop, rule)
      : { width: crop.width, height: crop.height };

    canvas.width = Math.max(output.width, 1);
    canvas.height = Math.max(output.height, 1);
    // 九参 drawImage：源矩形取自**视频像素坐标**（已按 cover 修正），
    // 目标矩形铺满画布 —— 证件照在这一步完成「裁成一寸 + 标准化尺寸」
    context.drawImage(
      video,
      crop.x,
      crop.y,
      crop.width,
      crop.height,
      0,
      0,
      canvas.width,
      canvas.height,
    );
    try {
      const blob = await toJpegBlob(canvas);
      close();
      onCapture(blob);
    } catch (error) {
      setErrorText(error instanceof Error ? error.message : '画面生成失败，请重试');
    }
  }, [close, mask, onCapture, streaming]);

  const onFilePicked = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;
      /*
       * 不管来自系统相机还是相册，都先归一化再上传（2026-10-27 真机事故）。
       *
       * 原先这里直接 `onCapture(file)`，把手机给的原始文件原样传上去。同一个账号
       * 换一台手机就报「服务异常」，服务端日志是
       * `人像分割返回错误 error_code=216202 msg=image size error` ——
       * 新机拍出来的 8000+ 长边、10MB 级原图超过了供应商上限。
       * 实时取景那条路走 canvas 出图，天然没有这个问题，所以只有相册/系统相机中招。
       */
      setErrorText(null);
      setPreparing(true);
      try {
        const normalized = await normalizeImage(file);
        // 降级路径（系统相机 / 相册）拿到的是一张成品图，没有取景框可依据，
        // 因此**不裁** —— 硬按上次的框去裁会把用户拍好的证件切歪。
        close();
        onCapture(normalized);
      } catch (error) {
        // 留在浮层里报错：关掉了就没有地方显示这句话，学员只会觉得「点了没反应」。
        // 强制现拍（`allowAlbum=false`）路径本来不开浮层，这里把它打开 —— R-06 不做静默失败。
        setErrorText(error instanceof Error ? error.message : '照片处理失败，请重新拍摄');
        setOpen(true);
      } finally {
        setPreparing(false);
      }
    },
    [close, onCapture],
  );

  return (
    <>
      <button
        type="button"
        className={triggerClassName}
        disabled={disabled}
        onClick={openCamera}
        // 不给 aria-label：触发器里的可见文字就是它的名字（写成 hint 会让读屏把
        // 「请将身份证人像面放入取景框」当成按钮名，与屏幕上写的「拍摄」对不上）
      >
        {children}
      </button>

      {open ? (
        <div
          className="camera-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={hint}
          // 排查用：真机反馈「拍不了」时先看这一格，别再从 UA 猜
          data-camera=""
          data-live={env.canLiveCapture ? '1' : '0'}
          data-reason={env.reason}
        >
          <div className="camera-overlay__bar">
            <button type="button" className="camera-overlay__close" onClick={close}>
              取消
            </button>
            {/*
              降级态标题写「取景示例」而不是 `hint`：
              ① 先告诉学员「这不是实时画面」，否则他会等一个永远不来的快门；
              ② `hint` 那句（「请正对镜头，头部居中，露出双肩」）与蒙版下方那句
                 `FALLBACK_TIP` 是同一件事，并排出现就是复读。
              构图要求交给蒙版 + `FALLBACK_TIP`，标题只负责定性。
            */}
            <span className="camera-overlay__hint">{env.canLiveCapture ? hint : '取景示例'}</span>
            <span className="camera-overlay__close" aria-hidden="true" />
          </div>
          {env.canLiveCapture ? (
            <div className="camera-overlay__stage">
              {streaming ? (
                <video ref={videoRef} className="camera-overlay__video" playsInline muted />
              ) : (
                <div className="camera-overlay__placeholder">未获取到取景画面</div>
              )}
              <div
                ref={frameRef}
                className={`camera-overlay__frame${
                  mask === 'portrait'
                    ? ' camera-overlay__frame--portrait'
                    : facing === 'user'
                      ? ' camera-overlay__frame--round'
                      : ''
                }`}
                aria-hidden="true"
              >
                {mask === 'portrait' ? (
                  <PortraitMask />
                ) : mask ? (
                  <MaskArtwork variant={mask} />
                ) : null}
              </div>
              <canvas ref={canvasRef} style={{ display: 'none' }} />
            </div>
          ) : (
            /*
              系统相机形态：没有取景画面，所以**不摆取景框**（摆了就是一个永远不动的
              空框，学员会等快门）。但也不能只给一句话 —— 2026-10-26 真机反馈：
              「请用手机相机拍摄」这句没告诉他**怎么拍**，拍完才发现构图不对又要重来。

              折中做法：给一张**静态示例蒙版**（复用同一套 SVG，尺寸同画幅比例），
              把「头部居中 / 四角入框」这些要求画出来。它是示意图，不是取景框 ——
              靠两处与实时态区分：边框用**虚线**，顶栏标题换成「取景示例」（见上）。
            */
            <div className="camera-overlay__fallback">
              <div
                className={`camera-overlay__demo${
                  mask === 'portrait' ? ' camera-overlay__demo--portrait' : ''
                }`}
                data-demo={mask ?? 'none'}
                aria-hidden="true"
              >
                {mask === 'portrait' ? (
                  <PortraitMask />
                ) : mask ? (
                  <MaskArtwork variant={mask} />
                ) : (
                  <span className="camera-overlay__demo-icon">
                    <CameraIcon size={30} />
                  </span>
                )}
              </div>
              <p className="camera-overlay__demo-tip">{FALLBACK_TIP[mask ?? 'none']}</p>
              <p className="camera-overlay__fallback-text">
                {describeLiveCaptureFallback(env.reason)}
              </p>
            </div>
          )}
          <div className="camera-overlay__foot">
            {errorText ? <div className="camera-overlay__error">{errorText}</div> : null}
            <div className="camera-overlay__actions">
              {env.canLiveCapture ? (
                <>
                  {/* 取景可用时，「相册」只是「已经有图就不用重拍」的补充入口 */}
                  {allowAlbum ? (
                    <Button
                      variant="ghost"
                      disabled={disabled || preparing}
                      onClick={() => albumRef.current?.click()}
                    >
                      相册
                    </Button>
                  ) : null}
                  {/*
                    取流失败（权限被拒 / 无摄像头）时补一个系统相机兜底。
                    平时不出现 —— 有实时画面时它就是多余的第二入口，
                    正是这一步让浮层从「两个重复按钮」变成「一个主按钮」。
                  */}
                  {errorText ? (
                    <Button
                      variant="ghost"
                      disabled={disabled || preparing}
                      onClick={() => fileRef.current?.click()}
                    >
                      系统相机
                    </Button>
                  ) : null}
                  <Button
                    variant="primary"
                    disabled={disabled || !streaming}
                    onClick={() => void shoot()}
                  >
                    {actionText}
                  </Button>
                </>
              ) : (
                // 无实时取景：只给一个动作 —— 微信内是「相册」（弹拍照 / 选图），
                // 非微信是「系统相机」（直进相机）
                <Button variant="primary" disabled={disabled || preparing} onClick={pickFallback}>
                  {preparing ? '正在处理照片…' : fallbackAction.label}
                </Button>
              )}
            </div>
            <div className="camera-overlay__note">
              {env.canLiveCapture
                ? allowAlbum
                  ? '可拍摄，也可从相册选择已有证件照'
                  : '仅支持相机实拍，不可从相册选择'
                : fallbackAction.note}
            </div>
          </div>
        </div>
      ) : null}

      {/*
        ⚠️ 两个隐藏 input 必须**常驻 DOM**，不能挂在浮层里（2026-10-26 真机事故）。

        证件照那条路径（`allowAlbum === false`）**不开浮层**就直接 `click()`，
        而浮层是 `open ? ... : null` —— input 挂在浮层内部时，`fileRef.current` 恒为 null，
        `?.click()` 静默什么都不做。学员看到的就是「点了『立即拍摄』完全没反应」。
        常驻之后，`openCamera` 的三条分支都能拿到节点。

        降级入口（`fileRef`）：只带 `capture`，不带 `accept`。
        `capture` 跟随 `facing` —— 自拍场景写死后置等于逼用户把手机转过去。

        相册入口（`albumRef`）：带 `accept="image/*"`、不带 `capture`。
        微信内点它会弹「拍照 / 从相册选择」两个选项（等于把系统相机也一起给了），
        因此微信内不再另设「系统相机」按钮（见文件头按钮矩阵）。
      */}
      <input
        ref={fileRef}
        type="file"
        capture={facing}
        style={{ display: 'none' }}
        onChange={onFilePicked}
      />
      {allowAlbum ? (
        <input
          ref={albumRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={onFilePicked}
        />
      ) : null}
    </>
  );
}
